import React, { useEffect, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { authState } from '../store/atoms';
import { useAuth } from '../hooks/useAuth';
import AppOnly from '../pages/user/AppOnly';

/**
 * 회원 페이지 접근 정책:
 * - PC/노트북 웹: 평소처럼 회원 페이지를 보여 준다 (반응형 웹 그대로).
 * - 모바일 브라우저(휴대폰): 앱 설치 안내 (AppOnly) 화면으로 차단.
 * - 네이티브 앱(WebView 안 또는 향후 외부 React 렌더): 통과.
 * - `?web=1` 쿼리: 모바일이라도 우회해서 웹 회원 페이지를 보고 싶을 때.
 *
 * iPad: iPadOS 13+ 는 데스크톱 Safari 와 같은 UA 를 주므로 maxTouchPoints
 * 로 분기. 사용자 요청은 "휴대폰만 차단" 이므로 iPad 도 데스크톱으로 본다.
 */
const shouldShowAppOnly = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator?.userAgent ?? '';

  // 1) 네이티브 앱 안에서 띄우는 경우 — 통과
  if (ua.includes('DreamDrumsApp')) return false;
  // 2) ?web=1 우회 — 통과
  const params = new URLSearchParams(window.location.search);
  if (params.get('web') === '1') return false;

  // 3) 휴대폰만 차단. iPad/태블릿은 데스크톱으로 취급.
  const isPhone = /Android.*Mobile|iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  return isPhone;
};

interface UserRouteProps {
  children: React.ReactNode;
}

const UserRoute: React.FC<UserRouteProps> = ({ children }) => {
  // hooks 는 모든 렌더에서 동일 순서로 호출. 분기 렌더는 맨 아래에서.
  const auth = useAtomValue(authState);
  const { validateToken } = useAuth();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const hasValidated = useRef(false);
  // 첫 렌더에 한 번 계산. UA 는 세션 중 바뀌지 않으므로 ref 로 안정화.
  const showAppOnly = useRef(shouldShowAppOnly()).current;

  useEffect(() => {
    const validateTokenAsync = async () => {
      // 이미 검증되었으면 중복 실행 방지
      if (hasValidated.current) {
        return;
      }

      try {
        // 로그인하지 않은 경우
        if (!auth.isAuthenticated) {
          setIsValid(false);
          setIsValidating(false);
          hasValidated.current = true;
          return;
        }

        // 사용자가 아닌 경우
        if (auth.userType !== 'user') {
          setIsValid(false);
          setIsValidating(false);
          hasValidated.current = true;
          return;
        }

        // 서버 토큰 검증 (최초 1회만, 캐싱 적용)
        if (!hasValidated.current) {
          const isValid = await validateToken();
          setIsValid(isValid);
          hasValidated.current = true;
        }
      } catch {
        setIsValid(false);
        hasValidated.current = true;
      } finally {
        setIsValidating(false);
      }
    };

    validateTokenAsync();
  }, [auth.isAuthenticated, auth.userType, validateToken]);

  // 휴대폰 브라우저면 인증 결과와 무관하게 안내 화면.
  if (showAppOnly) {
    return <AppOnly />;
  }

  // 검증 중일 때는 로딩 표시
  if (isValidating) {
    return (
      <div className="page-loading">
        <div className="loading-spinner"></div>
        <p>권한 확인 중...</p>
      </div>
    );
  }

  // 검증 실패 시 리다이렉트
  if (!isValid) {
    if (!auth.isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    else {
      // 권한이 없거나 검증 실패 시 로그인 페이지로 이동
      return <Navigate to="/login" replace />;
    }
  }

  return <>{children}</>;
};

export default UserRoute;
